import { T, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { AlertTriangle } from 'lucide-react'

import {
  checkRate,
  formatRate,
  isCustomRate,
  rateProblem,
  type RateProblem,
  type rateOptions,
  type VatCategory,
} from './vat-rates'

/**
 * The register's rate pickers - FRONTEND.md §9.327.
 *
 * **Moved here from `manager-kasa/fiskal-settings/FiskalSettingsPage` with the guard they call.**
 * §9.324 kept them on *Pokladňa → Nastavenia* because moving them would have orphaned
 * `checkRate` / `rateProblem`; the owner asked twice for one place - *"I am pretty sure you can do
 * it only in one place"* - so the guard moved too. Deleting it was never an option: `rateProblem`
 * catches a rate banding onto a VAT letter configured for a **different** rate, which is a receipt
 * that looks correct and registers the wrong tax (FB-070).
 *
 * <p>Unchanged from the version that shipped, deliberately. This is a move, not a redesign: the
 * markup, the aria labels and the `data-rate-warning` hook are the same, so the four e2e tests that
 * cover it move with it and keep asserting the same behaviour.
 */

const SELECT_CLASSES =
  'bg-background border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20'

const CUSTOM = '__custom__'

export function RegisterRatePicker({
  value,
  onChange,
  options,
  categories,
  label,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  options: ReturnType<typeof rateOptions>
  categories: VatCategory[]
  label: string
  disabled?: boolean
}) {
  const custom = isCustomRate(value, options)
  const check = checkRate(value, categories)
  const problem = rateProblem(check)

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <select
          value={custom ? CUSTOM : formatRate(check.rate ?? 0)}
          aria-label={label}
          disabled={disabled}
          onChange={(event) => event.target.value !== CUSTOM && onChange(event.target.value)}
          className={cn(SELECT_CLASSES, 'flex-1 disabled:opacity-50')}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
              {option.slotConfirmed ? '' : tt(' - bez indexu na kase')}
            </option>
          ))}
          {custom && (
            <option value={CUSTOM}>
              {value ? `${value} %` : tt('nenastavené')} {tt('(ručne)')}
            </option>
          )}
        </select>
        <input
          type="number"
          min="0"
          max="100"
          step="1"
          value={value}
          aria-label={`${label} - %`}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="w-20 bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-center outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
        />
      </div>

      {check.letter && !problem && (
        <p className="text-[11px] text-muted-foreground">
          <T>Vytlačí sa pod písmenom</T> <strong>{check.letter}</strong>
          {check.category?.name ? ` - ${check.category.name}` : ''}
        </p>
      )}
      {problem && <RateWarning problem={problem} rate={check.rate} />}
    </div>
  )
}

function RateWarning({ problem, rate }: { problem: RateProblem; rate: number | null }) {
  return (
    <p className="flex items-start gap-1.5 text-[11px] text-amber-500" data-rate-warning>
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
      <span>
        {problem.kind === 'not-a-rate' && <T>Toto nie je platná sadzba DPH.</T>}

        {problem.kind === 'letter-is-another-rate' && (
          <>
            <T>Sadzba</T> {rate === null ? '' : formatRate(rate)}{' '}
            <T>% sa vytlačí pod písmenom</T> <strong>{problem.letter}</strong>,{' '}
            <T>ktoré je u vás nastavené na</T> {formatRate(problem.configuredRate)}{' '}
            <T>%. Doklad by vyzeral správne a zaregistroval by inú daň.</T>
          </>
        )}

        {problem.kind === 'slot-unconfirmed' && (
          <>
            <T>Písmeno</T> <strong>{problem.letter}</strong>{' '}
            <T>
              nemá potvrdený index na pokladni, takže doklad s touto sadzbou sa nevytlačí. Index
              zistíte príkazom FRINFO v diagnostike na obrazovke Pokladňa.
            </T>
          </>
        )}
      </span>
    </p>
  )
}
